/**
 * useLocalFonts Hook
 *
 * Queries system fonts using the Local Font Access API.
 * Falls back to a curated list on unsupported browsers.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  FontOption,
  FontData,
  DEFAULT_FONTS,
  isLocalFontAccessSupported,
} from '@/types/fonts';

interface UseLocalFontsResult {
  fonts: FontOption[];
  isLoading: boolean;
  error: string | null;
  isSystemFontsSupported: boolean;
  hasPermission: boolean;
  requestPermission: () => Promise<void>;
}

/**
 * Deduplicate fonts by family name, keeping first occurrence
 */
function deduplicateFonts(fonts: FontOption[]): FontOption[] {
  const seen = new Set<string>();
  return fonts.filter(font => {
    const key = font.family.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Group FontData by family and collect styles
 */
function groupFontsByFamily(fontDataList: FontData[]): FontOption[] {
  const familyMap = new Map<string, FontOption>();

  for (const fontData of fontDataList) {
    const existing = familyMap.get(fontData.family);
    if (existing) {
      if (!existing.styles?.includes(fontData.style)) {
        existing.styles = [...(existing.styles || []), fontData.style];
      }
    } else {
      familyMap.set(fontData.family, {
        family: fontData.family,
        displayName: fontData.family,
        source: 'system',
        styles: [fontData.style],
      });
    }
  }

  return Array.from(familyMap.values());
}

/**
 * Sort fonts alphabetically, with common fonts first
 */
function sortFonts(fonts: FontOption[]): FontOption[] {
  const priorityFonts = new Set([
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
    'Arial', 'Helvetica', 'Helvetica Neue', 'SF Pro', 'San Francisco',
    'Segoe UI', 'system-ui',
  ]);

  return fonts.sort((a, b) => {
    const aPriority = priorityFonts.has(a.family);
    const bPriority = priorityFonts.has(b.family);

    if (aPriority && !bPriority) return -1;
    if (!aPriority && bPriority) return 1;

    return a.displayName.localeCompare(b.displayName);
  });
}

export function useLocalFonts(): UseLocalFontsResult {
  const [fonts, setFonts] = useState<FontOption[]>(DEFAULT_FONTS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);

  const isSupported = isLocalFontAccessSupported();

  const queryFonts = useCallback(async () => {
    if (!isSupported || !window.queryLocalFonts) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fontDataList = await window.queryLocalFonts();
      const systemFonts = groupFontsByFamily(fontDataList);

      // Merge with default fonts (system fonts first, then defaults as fallback)
      const allFonts = deduplicateFonts([...systemFonts, ...DEFAULT_FONTS]);
      const sorted = sortFonts(allFonts);

      setFonts(sorted);
      setHasPermission(true);
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          // User denied permission
          setError('Font access denied. Using default fonts.');
          setHasPermission(false);
        } else {
          setError(`Failed to load fonts: ${err.message}`);
        }
      }
      // Keep default fonts on error
      setFonts(DEFAULT_FONTS);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError('System fonts not supported in this browser');
      return;
    }
    await queryFonts();
  }, [isSupported, queryFonts]);

  // Auto-query on mount if API is available
  // Note: This will trigger permission prompt, so we don't do it automatically
  // User must click "Load System Fonts" button
  useEffect(() => {
    // Check if we already have permission (browser remembers)
    // We can't actually check without prompting, so just set supported state
  }, []);

  return {
    fonts,
    isLoading,
    error,
    isSystemFontsSupported: isSupported,
    hasPermission,
    requestPermission,
  };
}

/**
 * Hook for paginated font list with search
 */
export function usePaginatedFonts(
  fonts: FontOption[],
  pageSize: number = 50
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  // Filter fonts by search query
  const filteredFonts = searchQuery
    ? fonts.filter(font =>
        font.displayName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : fonts;

  // Paginate
  const totalPages = Math.ceil(filteredFonts.length / pageSize);
  const paginatedFonts = filteredFonts.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return {
    fonts: paginatedFonts,
    totalFonts: filteredFonts.length,
    page,
    totalPages,
    setPage,
    searchQuery,
    setSearchQuery,
    hasNextPage: page < totalPages - 1,
    hasPrevPage: page > 0,
    nextPage: () => setPage(p => Math.min(p + 1, totalPages - 1)),
    prevPage: () => setPage(p => Math.max(p - 1, 0)),
  };
}
